import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';

const GLOBAL_PHONES = [
  {
    countryKey: 'footer.uk',
    flag: '🇬🇧',
    number: '+14342647625',
    href: 'tel:+14342647625',
  },
  {
    countryKey: 'footer.usa',
    flag: '🇺🇸',
    number: '+1 (434) 264-7625',
    href: 'tel:+14342647625',
  },
  {
    countryKey: 'footer.canada',
    flag: '🇨🇦',
    number: '+1 (226) 801-0117',
    href: 'tel:+12268010117',
  },
];

export default function Footer() {
  const { settings } = useSiteSettings();
  const { t } = useTranslation();

  return (
    <footer style={{ backgroundColor: '#0d2b2b' }} className="text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--primary)' }}
              >
                <i className="ri-flight-takeoff-line text-white text-lg" />
              </div>
              <span className="font-heading font-bold text-xl">{settings.companyName}</span>
            </div>
            <p className="text-teal-200/70 text-sm leading-relaxed max-w-xs">
              {settings.companyTagline} {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-4 mt-6">
              {['ri-twitter-x-fill', 'ri-linkedin-fill', 'ri-facebook-fill', 'ri-instagram-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider text-teal-300 uppercase mb-5">
              <a href="#quick-links">{t('footer.quickLinks')}</a>
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/', labelKey: 'nav.home' },
                { to: '/track', labelKey: 'nav.track' },
                { to: '/about', labelKey: 'nav.about' },
                { to: '/contact', labelKey: 'nav.contact' },
              ].map(({ to, labelKey }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-teal-200/70 hover:text-white transition-colors"
                  >
                    {t(labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider text-teal-300 uppercase mb-5">
              <a href="#contact-info">{t('footer.contactUs')}</a>
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-map-pin-line text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-teal-200/70">{settings.companyAddress}</span>
              </li>
              <li className="flex items-center gap-3">
                <i className="ri-phone-line text-primary flex-shrink-0" />
                <a href={`tel:${settings.companyPhone}`} className="text-sm text-teal-200/70 hover:text-white transition-colors">
                  {settings.companyPhone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="ri-mail-line text-primary flex-shrink-0" />
                <a href={`mailto:${settings.companyEmail}`} className="text-sm text-teal-200/70 hover:text-white transition-colors">
                  {settings.companyEmail}
                </a>
              </li>
            </ul>
          </div>

          {/* Global Phone Numbers */}
          <div>
            <h4 className="font-semibold text-sm tracking-wider text-teal-300 uppercase mb-5">
              <a href="#global-numbers">{t('footer.globalNumbers')}</a>
            </h4>
            <ul className="space-y-4">
              {GLOBAL_PHONES.map(({ countryKey, flag, number, href }) => (
                <li key={countryKey} className="flex items-start gap-3">
                  <span className="text-lg leading-none mt-0.5 flex-shrink-0">{flag}</span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-teal-300 uppercase tracking-wide">
                      {t(countryKey)}
                    </span>
                    <a
                      href={href}
                      className="text-sm text-teal-200/70 hover:text-white transition-colors font-mono whitespace-nowrap"
                    >
                      {number}
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-teal-200/50">
            &copy; {new Date().getFullYear()} {settings.companyName}. {t('footer.allRightsReserved')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-xs text-teal-200/50 hover:text-white transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms-of-service" className="text-xs text-teal-200/50 hover:text-white transition-colors">
              {t('footer.terms')}
            </Link>
            <Link to="/cookie-policy" className="text-xs text-teal-200/50 hover:text-white transition-colors">
              {t('footer.cookies')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
